import React, { useState, useEffect, useMemo } from "react";

import Box from '@mui/material/Box';

import "../App.css"
import {useAppCtx} from "../app/AppCtx";
import {useCrewCtx} from "../app/CrewCtx";
import {PageHeader} from "./PageHeader";
import {CxDataGrid} from "../cmx/CxData";
import {CxGrid} from "../cmx/CxGrid";
import {CxListFeedback} from "../cmx/CxFeedback";
import {CxText} from "../cmx/CxTypography"
import {Project} from "../domain/Project";
import {runningCall, successCall} from "../gateway/CallService";
import {projectViewList} from "../gateway/ProjectService"
import {CxComboxSelection} from "../cmx/CxForm";
import {errorStatus, pendingStatus, successStatus} from "../ctx/AppStatus";
import {accessCtrl, canEditProjectInList} from "../ctx/AccessCtrl";
import {useCodeCtx} from "../app/CodeCtx";
import {CxSwitch} from "../cmx/CxSelection";

export function ProjectList() {
    const {handleAction, getPageState, setPageState, updateAppStatus} = useAppCtx();
    const {lazyloadCodes} = useCodeCtx();
    const {crew, lazyloadCrews} = useCrewCtx();

    useEffect(() => {
        lazyloadCodes();
        lazyloadCrews();
    }, []);


    // ----------------------------------------------------------------------------------------------------------------
    // header and view handling
    // ----------------------------------------------------------------------------------------------------------------

    const viewConfig = {
        assignView: {
            title   : "Projekte erstellen & zuweisen",
            desc    : "Ansicht aller aktiven Projekte ohne Bauführer. Dient zum Erstellen und Zuweisen neuer Projekte.",
            hasCtrl : false,
            query   : "projectAssignList"
        },
        planView: {
            title   : "Projekte planen",
            desc    : "Ansicht aller aktiven Projekte mit Bauführer, jedoch ohne Arbeitsvorgänge. Dient zur Planung und Erstellung neuer Aufgaben.",
            hasCtrl : true,
            query   : "projectPlanList"
        },
        workView: {
            title   : "Projekte bearbeiten",
            desc    : "Ansicht aller aktiven Projekte mit Bauführer (mit oder ohne Arbeitsvorgänge). Dient zur laufenden Bearbeitung und Fortschrittskontrolle.",
            hasCtrl : true,
            query   : "projectWorkList"
        },
        closeView: { // wird aktuell nicht mehr genutzt (Query auf Server ist noch vorhanden).
            title   : "Projekte nachbearbeiten",
            desc    : "Ansicht aller aktiven Projekte, deren Arbeitsvorgänge abgelaufen sind. Dient zum Projektabschluss oder zur Planung weiterer Etappen.",
            hasCtrl : true,
            query   : "projectCloseList"
        }
    }

    const view = getPageState('ProjectList', 'view');
    const cfg  = viewConfig[view];

    const handleViewChange = (value) => {
        setPageState('ProjectList', 'view', value);
    };

    const viewItems = [
        { label: "zuweisen",       value: "assignView" },
        { label: "planen",         value: "planView" },
        { label: "bearbeiten",     value: "workView" },
        { label: "nachbearbeiten", value: "closeView" }
    ];

    const actionItems = [
        { kind: "icon", label: "Erstellen", iconName: "create", actionName: "projectCreate", disabled: !accessCtrl.canCreateProject },
        ...(accessCtrl.canViewAvorProjectList ? [
            { kind: "tooltip", label: "Vorausmass", iconName: "avor", actionName: "projectAvor" }
        ] : []),
        { kind: "tooltip", label: "Projekte suchen", iconName: "search", actionName: "projectSearch" }
    ];

    const renderHeader = () => {
        return (
            <>
                <PageHeader
                    title={cfg.title}
                    viewItems={viewItems}
                    viewInitialValue={view}
                    viewOnChangeHandler={handleViewChange}
                    actionItems={actionItems}
                />
                <CxText>{cfg.desc}</CxText>
            </>
        );
    }

    // ----------------------------------------------------------------------------------------------------------------
    // control panel
    // ----------------------------------------------------------------------------------------------------------------

    const deputy = getPageState('ProjectList', 'deputy');
    const foremanFilter = getPageState('ProjectList', 'foremanFilter');

    const handleDeputyViewChange = (value) => {
        setPageState('ProjectList', 'deputy', value);
    }

    const handleForemanChange = (value) => {
        setPageState('ProjectList', 'foremanFilter', value);
    };

    const renderCtrlFilterForeman = () => {
        const name = deputy ? "Bauführer Stellvertreter" : "Bauführer";
        return (
            <CxComboxSelection
                id="foreman"
                name={name}
                value={foremanFilter}
                onChange={handleForemanChange}
                items={crew.getForemanSelection(true,"Alle")}
            />
        );
    }

    const renderCtrl = () => {
        return (
            <CxGrid container spacing={2} direction="row">
                <CxGrid size="xl">
                    <CxGrid size="md">
                        <CxSwitch name="deputy" label="Stellvertreter" checked={deputy}
                                  onChange={handleDeputyViewChange} />
                    </CxGrid>
                </CxGrid>
               <CxGrid size="xl">
                    <CxGrid size="md">
                        {renderCtrlFilterForeman()}
                    </CxGrid>
                </CxGrid>
            </CxGrid>
        );
    }

    // ----------------------------------------------------------------------------------------------------------------
    // select view data
    // ----------------------------------------------------------------------------------------------------------------

    const [call, setCall] = useState(runningCall());

    useEffect(() => {
        // TODO MAINTENANCE: add active flag and useEffect response
        setCall(runningCall())
        updateAppStatus('call', pendingStatus("Project data load is pending."));

        projectViewList(cfg.query).then(responseCall => {
            if (responseCall.isSuccess) {
                const mappedRows = Project.mapListColumns(responseCall.result, crew);
                setCall(successCall(mappedRows));
                updateAppStatus('call', successStatus(`Project data ${responseCall.result.length} records successfully loaded.`, responseCall.elapsed));
            } else {
                setCall(responseCall);
                updateAppStatus('call', errorStatus(`Project data load failed with error: ${responseCall.result}`, responseCall.elapsed));
            }
        });

    }, [view]);

    // ----------------------------------------------------------------------------------------------------------------
    // filter data
    // ----------------------------------------------------------------------------------------------------------------

    const rows = useMemo(() => {
        if (call.isSuccess) {

            return call.result.filter((project) => {

                if (cfg.hasCtrl && foremanFilter !== -1) {
                    if (deputy) {
                        if (project.deputy_foreman_crew_id !== foremanFilter) {
                            return false;
                        }
                    } else {
                        if (project.foreman_crew_id !== foremanFilter) {
                            return false;
                        }
                    }
                }

                return true;
            });
        }
        return [];

    }, [call, cfg, deputy, foremanFilter]);

    // ----------------------------------------------------------------------------------------------------------------
    // render view
    // ----------------------------------------------------------------------------------------------------------------

    const handleRowDoubleClick = (params) => {
        handleAction("projectDetail", params.row);
    };

    const handleEditClick = (params) => {
        handleAction("projectUpdate", params.row, "project");       // return back to 'project' view after edit
    }

    return (
        <Box sx={{ width: '100%' }}>
            <CxGrid container spacing={2}>

                { renderHeader() }
                { cfg.hasCtrl && renderCtrl() }

                <CxListFeedback call={call} />

                { call.isSuccess &&
                    <CxGrid size="xl">
                        <CxDataGrid
                            rows={rows}
                            columns={Project.getListColumns(false, false)}
                            onRowDoubleClick={handleRowDoubleClick}
                            onViewAction={handleRowDoubleClick}
                            onEditAction={canEditProjectInList(foremanFilter, view) ? handleEditClick : undefined} />
                    </CxGrid>
                }

            </CxGrid>
        </Box>
    );
}

// --------------------------------------------------------------------------------------------------------------------
// The end.